import Head from 'next/head';

import {HeadSEO} from '@udemy/head-seo';
import {useI18n} from '@udemy/i18n';
import {ExperimentationProvider} from '@udemy/react-experimentation-provider';
import {SubscriptionPlanProductType, SubscriptionPlansProvider} from '@udemy/subscription-graphql';

import {PageProps} from 'src/types/page-props';

import {useCanonicalUrl} from '../../hooks/useCanonicalUrl';
import {META_DEFAULT_BASE_URL} from '../../lib/constants';
import {
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    VALUE_PLANSTARTER_PLAN,
} from '../../lib/experimentation-constants';
import {
    CareerAcademyLandingPage,
    CareerAcademyLandingPageProps,
} from './career-academy-landing-page.react-component';
import {CTAContextProvider} from './components/cta-context/cta-context';

export type CareerAcademyLandingPageFeatureProps = PageProps & CareerAcademyLandingPageProps;

export const careerAcademyLandingPageExperimentationFeatures = [
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    VALUE_PLANSTARTER_PLAN,
];

export function CareerAcademyLandingPageFeature(props: CareerAcademyLandingPageFeatureProps) {
    const {interpolate, gettext} = useI18n();
    const {url: canonicalUrl} = useCanonicalUrl([], META_DEFAULT_BASE_URL);

    const metaProps = {
        title: interpolate(
            gettext('%(careerName)s Career Accelerator | Udemy'),
            {careerName: props.academy.career.name},
            true,
        ),
        description: props.academy.seoDescription ?? props.academy.primaryDescription,
    };

    return (
        <>
            <HeadSEO
                title={metaProps.title}
                ogTitle={metaProps.title}
                description={metaProps.description}
                ogDescription={metaProps.description}
                ogLocale={process.env.I18N_PATH_TO_BUNDLE_MAP[props.locale]}
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <ExperimentationProvider features={careerAcademyLandingPageExperimentationFeatures}>
                <SubscriptionPlansProvider
                    subscriptionPlanQueryVariables={[
                        {
                            filters: {
                                productType:
                                    SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
                            },
                        },
                    ]}
                >
                    <CTAContextProvider
                        acceleratorId={props.academy.numericId}
                        courseIds={props.courses?.map((course) => course.id) ?? []}
                    >
                        <CareerAcademyLandingPage {...props} />
                    </CTAContextProvider>
                </SubscriptionPlansProvider>
            </ExperimentationProvider>
        </>
    );
}
